import React from 'react';
import PropTypes from 'prop-types';
import RpTable from 'rp-react-table';
import { flatten, toUpper, toLower, without } from 'lodash';
import Filter from 'rp-react-common/Filter';

import categoriesReducer from './categoriesReducer';
import { categoriesFactory, statusFactory } from './categories';
import { statsColumns, createDynamicColumn } from './columns';

import FilteringComponent from '../../../shared/filter';
import { selection } from '../../../utils/filter';
import { getCategories } from '../../../utils/storage';

class Stats extends FilteringComponent {
    constructor(props) {
        super(props);

        const {
            statistics,
            statisticalSummary,
            seasonInfo,
            seasonsAvailable,
        } = props.data;

        const categories = categoriesFactory(
            seasonInfo.countryCode,
            seasonInfo.raceType,
            props.params.type,
            seasonsAvailable
        );
        const status = statusFactory(categories);

        this.state = {
            tableData: statistics || statisticalSummary || [],
            loading: false,
            fetchError: false,
            categories,
            status,
        };
    }

    componentDidMount() {
        const { params: { tab, type } } = this.props;
        const categories = getCategories(tab, type, categoriesReducer, this.state.categories);
        if (categories) {
            this.onFilterApply(categories);
        }
    }

    onDataReceive = ({ data: { statistics, statisticalSummary } }) => {
        const { categories } = this.state;
        const selectedAdvanced = selection.getValue(categories, 'advanced');
        const selectedDisplayBy = selection.getValue(categories, 'displayBy');

        let tableData = [];
        if (selectedDisplayBy === 'age-of-horse' || selectedDisplayBy === 'race-category') {
            tableData = flatten(Object.values(statistics));
        } else {
            tableData = (statistics ?
                statistics[toUpper(selectedAdvanced)] :
                statisticalSummary) || [];
        }

        this.setState({
            tableData,
            loading: false,
            status: statusFactory(categories),
        });
    };

    buildUrl = ({ timePeriod, raceType, displayBy }) => {
        const { id, type } = this.props.params;
        const commonPart = `filter/${type}/${id}/stats`;

        const years = timePeriod.split('/').slice(0, 2).join('/');
        if (displayBy === 'year' || displayBy === 'season') {
            return `${commonPart}/summary/${years}/${raceType}`;
        }
        const racingTypeWithDisplayBy = raceType.match(/turf|aw/) ?
            raceType.replace(/(turf|aw)/g, `${displayBy}/$1`) :
            `${raceType}/${displayBy}`;

        return `${commonPart}/statistics/${years}/${racingTypeWithDisplayBy}`;
    };

    renderStatus() {
        const { raceType, displayBy, timePeriod, advanced } = this.state.status;
        const displayByParts = displayBy.split('(');
        const displayByGeneral = toLower(displayByParts[0].trim());
        const displayByDesc = displayByParts[1];
        return (
            <h2 className="ui-filter__statusText">
                {raceType},&nbsp;
                {timePeriod},&nbsp;
                <strong>displayed by {displayByGeneral}</strong>&nbsp;
                {displayByDesc ? `(${displayByDesc}` : null}
                {advanced ? <span>({advanced})</span> : null}&nbsp;
            </h2>
        );
    }

    render() {
        const { tableData, loading, categories, fetchError } = this.state;

        const dynamicColumn = createDynamicColumn(categories);

        const isIre = selection.getValue(categories, 'raceType').includes('IRE');
        const statsCols = [dynamicColumn, ...statsColumns(isIre)];

        const overall = tableData.find(row => row.groupName === 'OVERALL');
        const statsData = without(tableData, overall);

        return (
            <div className="mp-statsTable">
                <Filter
                    categories={categories}
                    categoriesReducer={categoriesReducer}
                    columns={statsCols}
                    fetchError={fetchError}
                    onFilterApply={this.onFilterApply}
                />
                {!fetchError && (
                    <RpTable
                        data={statsData || []}
                        footerData={overall}
                        columns={statsCols}
                        sortable
                        noDataText="No stats found"
                        initialSortIdx={0}
                        loading={loading}
                    />
                )}
            </div>
        );
    }
}

const statsPros = PropTypes.arrayOf(PropTypes.shape({
    euroTotalPrize: PropTypes.number,
    euroWinPrize: PropTypes.number,
    place1stNumber: PropTypes.number,
    place2ndNumber: PropTypes.number,
    place3rdNumber: PropTypes.number,
    place4thNumber: PropTypes.number,
    raceNumber: PropTypes.number,
    seasonEndDate: PropTypes.string,
    seasonStartDate: PropTypes.string,
    stake: PropTypes.number,
    totalPrize: PropTypes.number,
    winPercent: PropTypes.number,
    winPrize: PropTypes.number,
}));

Stats.propTypes = {
    data: PropTypes.shape({
        stats: statsPros,
        statsResponse: statsPros,
        seasonInfo: PropTypes.shape({
            raceType: PropTypes.string.isRequired,
            countryCode: PropTypes.string.isRequired,
            seasonStartDate: PropTypes.string,
            seasonEndDate: PropTypes.string.isRequired,
        }).isRequired,
    }).isRequired,
    params: PropTypes.shape({
        id: PropTypes.string,
        type: PropTypes.string,
    }).isRequired,
};

export default Stats;



// WEBPACK FOOTER //
// ./src/js/human/stats/statsTable/StatsTable.jsx